import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ChatWidget } from '@/components/chat/ChatWidget';
import { Toaster } from 'sonner';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
import WidgetConfig from '@/pages/WidgetConfig';
import PricingPage from '@/pages/PricingPage';
import BillingPage from '@/pages/BillingPage';
import AdminPage from '@/pages/AdminPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import ChatHistoryPage from '@/pages/ChatHistoryPage';
import GuidePage from '@/pages/GuidePage';
import PolicyPage from '@/pages/PolicyPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import GoogleCallback from '@/pages/GoogleCallback';
import {
  MessageSquare, Database, Settings, Zap, Shield, Globe, Check,
  Code, Palette, BarChart3, Lock, Users, Clock, ArrowRight,
  Star, ChevronRight
} from 'lucide-react';
import { SplineScene } from '@/components/ui/splite';
import { Spotlight } from '@/components/ui/spotlight';
import { Card } from '@/components/ui/card';
import { CookieConsent } from '@/components/CookieConsent';

// ─── Protected Route ────────────────────────────────────
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ─── Logo Component ─────────────────────────────────────
function Logo({ size = 'md' }) {
  const s = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
  const iconS = size === 'sm' ? 14 : 18;
  return (
    <div className={`${s} rounded-lg bg-[#10B981] flex items-center justify-center`}>
      <svg width={iconS} height={iconS} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="10" width="3" height="8" rx="1.5" fill="white" opacity="0.7" />
        <rect x="8" y="6" width="3" height="16" rx="1.5" fill="white" />
        <rect x="13" y="3" width="3" height="18" rx="1.5" fill="white" />
        <rect x="18" y="8" width="3" height="10" rx="1.5" fill="white" opacity="0.7" />
      </svg>
    </div>
  );
}

// ─── Data ───────────────────────────────────────────────
const FEATURES = [
  { icon: MessageSquare, title: 'Multi-turn Conversations', description: 'Context-aware AI that remembers every message. Your customers feel heard, not lost in a loop.' },
  { icon: Database, title: 'Knowledge Base', description: 'Feed your bot company-specific Q&A pairs. It answers with your information, not generic responses.' },
  { icon: Palette, title: 'Full Brand Customization', description: 'Match your brand perfectly. Custom colors, themes, bot name, welcome message, and personality.' },
  { icon: Zap, title: 'Instant AI Responses', description: 'Powered by GPT-4o-mini. Sub-second response times that keep customers engaged 24/7.' },
  { icon: Code, title: 'One-Line Embed', description: 'Copy a single script tag, paste before </body>. Your AI widget is live in under 60 seconds.' },
  { icon: Globe, title: 'Dark & Light Modes', description: 'Automatic theme adaptation. Your widget looks native on any website, any design.' },
  { icon: Lock, title: 'Secure & Private', description: 'All data encrypted. Your knowledge base stays private. No customer data shared across accounts.' },
  { icon: BarChart3, title: 'Usage Analytics', description: 'Track message volume, monitor limits, and understand how customers interact with your bot.' },
  { icon: Users, title: 'Multi-Bot Management', description: 'Run different bots for different websites from one dashboard. Each with its own personality.' },
];

const STATS = [
  { value: '500ms', label: 'Avg response time' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '24/7', label: 'Always available' },
  { value: '60s', label: 'Setup time' },
];

const TESTIMONIALS = [
  { name: 'Priya S.', role: 'Founder, E-commerce', text: 'We replaced our entire support team\'s first-line responses with Whisper.AI. Response time went from 4 hours to instant.', rating: 5 },
  { name: 'Marcus D.', role: 'CTO, SaaS', text: 'The knowledge base feature is a game-changer. Our bot answers technical questions accurately because we trained it with our docs.', rating: 5 },
  { name: 'Sarah L.', role: 'Marketing Lead, Healthcare', text: 'Setup took literally 2 minutes. Pasted the script, configured the bot name, added FAQ pairs. Done. Our conversion rate jumped 23%.', rating: 5 },
];

const FAQS = [
  { q: 'How does the free plan work?', a: 'You get 1 chat bot with 50 messages per month, a knowledge base, and accent color customization. No credit card required. Upgrade anytime.' },
  { q: 'What is a "chat bot" in pricing terms?', a: 'Each chat bot is a unique AI assistant with its own name, personality, knowledge base, and embed code. You embed each one on a different website (or different pages of the same site).' },
  { q: 'Can I cancel anytime?', a: 'Yes. Cancel from your Billing page at any time. Your current plan stays active until the end of the billing period. No refunds for the current month.' },
  { q: 'How do I embed the widget?', a: 'Copy the one-line script tag from your dashboard and paste it before the closing </body> tag on your website. That\'s it — your AI bot goes live instantly.' },
  { q: 'Is my data secure?', a: 'Absolutely. Each account is isolated. Your knowledge base and conversation data are never shared. All API calls are encrypted via HTTPS.' },
  { q: 'What AI model powers the bot?', a: 'GPT-4o-mini by OpenAI — fast, accurate, and cost-effective. It handles multi-turn conversations naturally and responds in under 500ms.' },
];

// ─── Landing Page ───────────────────────────────────────
function LandingContent() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className={`min-h-screen relative ${theme === 'dark' ? 'landing-gradient-dark' : 'landing-gradient-light'} noise-overlay`}>
      {/* Navigation */}
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-5" data-testid="site-header">
        <div className="flex items-center gap-2.5">
          <Logo />
          <span className="text-lg font-bold tracking-tight text-foreground" data-testid="brand-logo">Whisper.AI</span>
        </div>
        <nav className="hidden md:flex items-center gap-6">
          <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Features</a>
          <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
          <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-pricing">Pricing</Link>
          <a href="#faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors">FAQ</a>
          <Link to="/guide" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-guide">Setup Guide</Link>
          <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-blog">Blog</Link>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg bg-[#10B981] text-white text-sm font-medium hover:bg-[#059669] transition-colors" data-testid="nav-dashboard-btn">Dashboard</button>
          ) : (
            <>
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-login">Sign In</Link>
              <Link to="/signup" className="px-4 py-2 rounded-lg bg-[#18181B] text-white text-sm font-medium hover:bg-[#27272A] transition-colors dark:bg-white dark:text-[#09090B] dark:hover:bg-gray-200" data-testid="nav-signup-btn">Get Started Free</Link>
            </>
          )}
        </div>
      </header>

      <main className="relative z-10 px-6 sm:px-10">

        {/* ─── Hero ─── */}
        <section className="max-w-6xl mx-auto pt-8 sm:pt-12 pb-12" aria-label="Hero">
          <Card className="w-full h-[500px] sm:h-[520px] bg-black/[0.96] relative overflow-hidden rounded-2xl border-border" data-testid="hero-3d-card">
            <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="#10B981" />
            <div className="flex h-full">
              <div className="flex-1 p-8 sm:p-12 relative z-10 flex flex-col justify-center">
                <div className="animate-fade-in-up">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#10B981]/15 text-[#10B981] text-xs font-medium tracking-wide mb-6">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                    Trusted by 500+ websites
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-neutral-50 to-neutral-400 leading-[1.1] animate-fade-in-up animate-delay-100" data-testid="hero-title">
                  Your AI assistant,<br />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#10B981] to-[#34D399]">on every website</span>
                </h1>
                <p className="mt-5 text-neutral-400 max-w-md text-sm sm:text-base leading-relaxed animate-fade-in-up animate-delay-200">
                  Deploy a GPT-powered chat widget in 60 seconds. Train it with your knowledge, match your brand, and let it handle customer conversations 24/7.
                </p>
                <div className="flex flex-wrap gap-3 mt-8 animate-fade-in-up animate-delay-300">
                  <button onClick={() => navigate(user ? '/dashboard' : '/signup')} className="px-6 py-3 rounded-xl bg-[#10B981] text-white text-sm font-medium hover:bg-[#059669] transition-colors flex items-center gap-2" data-testid="hero-cta-btn">
                    {user ? 'Go to Dashboard' : 'Start Free — No Credit Card'} <ArrowRight className="h-4 w-4" />
                  </button>
                  <button onClick={() => document.querySelector('[data-testid="chat-launcher-bubble"]')?.click()} className="px-6 py-3 rounded-xl border border-white/10 text-white/80 text-sm font-medium hover:bg-white/5 transition-colors" data-testid="try-demo-btn">
                    Try Live Demo
                  </button>
                </div>
                <p className="mt-4 text-neutral-500 text-xs animate-fade-in-up animate-delay-400">Free forever plan available. Setup in under 60 seconds.</p>
              </div>
              <div className="flex-1 relative hidden md:block">
                <SplineScene scene="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode" className="w-full h-full" />
              </div>
            </div>
          </Card>
        </section>

        {/* ─── Social Proof Stats ─── */}
        <section className="max-w-4xl mx-auto pb-16" aria-label="Statistics">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="stats-bar">
            {STATS.map((s) => (
              <div key={s.label} className="text-center py-4">
                <p className="text-2xl sm:text-3xl font-bold text-[#10B981]">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Embed Demo ─── */}
        <section className="max-w-4xl mx-auto pb-16" aria-label="Embed code">
          <div className="p-6 rounded-2xl border border-border bg-card/50 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-muted-foreground uppercase tracking-widest">One line to embed</p>
              <span className="text-xs text-[#10B981] font-medium">Copy &amp; paste. That's it.</span>
            </div>
            <code className="block text-sm bg-muted/80 p-4 rounded-xl text-foreground font-mono break-all" data-testid="embed-demo-code">
              {'<script src="https://whisperai.one/api/embed/widget.js?id=YOUR_WIDGET_ID"></script>'}
            </code>
            <p className="text-xs text-muted-foreground mt-3">Works with any website — WordPress, Shopify, Wix, React, HTML, and more.</p>
          </div>
        </section>

        {/* ─── Features ─── */}
        <section id="features" className="max-w-5xl mx-auto pb-24" aria-label="Features">
          <div className="text-center mb-12">
            <span className="text-xs text-muted-foreground uppercase tracking-widest">Features</span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mt-2 mb-3">Everything you need to automate support</h2>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">From real-time AI chat to brand customization and analytics. Built for businesses that want to scale without scaling headcount.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="features-grid">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="feature-card p-6 rounded-xl border border-border bg-card/50 backdrop-blur-sm animate-fade-in-up" style={{ animationDelay: `${0.3 + i * 0.06}s` }} data-testid={`feature-card-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-[#10B981]" />
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1.5">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── How It Works ─── */}
        <section id="how-it-works" className="max-w-4xl mx-auto pb-24" aria-label="How it works">
          <div className="text-center mb-12">
            <span className="text-xs text-muted-foreground uppercase tracking-widest">How It Works</span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mt-2 mb-3">Live in 3 steps, under 60 seconds</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8" data-testid="steps-grid">
            {[
              { step: '01', title: 'Create Your Bot', desc: 'Sign up free. Name your bot, set its personality, and add your company knowledge — Q&A pairs, FAQs, product info.', icon: Users },
              { step: '02', title: 'Customize Everything', desc: 'Pick your brand colors, choose dark or light theme, set a welcome message. Make it feel like your own product.', icon: Palette },
              { step: '03', title: 'Embed & Go Live', desc: 'Copy the one-line script tag from your dashboard. Paste it on your website. Your AI assistant is live instantly.', icon: Zap },
            ].map((s, i) => (
              <div key={s.step} className="text-center" data-testid={`step-${i}`}>
                <div className="w-14 h-14 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
                  <s.icon className="h-6 w-6 text-[#10B981]" />
                </div>
                <span className="text-[#10B981] text-xs font-bold tracking-wider">STEP {s.step}</span>
                <h3 className="text-base font-semibold text-foreground mt-2">{s.title}</h3>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Testimonials ─── */}
        <section className="max-w-5xl mx-auto pb-24" aria-label="Testimonials">
          <div className="text-center mb-12">
            <span className="text-xs text-muted-foreground uppercase tracking-widest">What People Say</span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mt-2">Trusted by growing businesses</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-4" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="p-6 rounded-xl border border-border bg-card/50" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-[#10B981] text-[#10B981]" />
                  ))}
                </div>
                <p className="text-sm text-foreground leading-relaxed mb-4">"{t.text}"</p>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Pricing Preview ─── */}
        <section className="max-w-4xl mx-auto pb-24 text-center" aria-label="Pricing">
          <span className="text-xs text-muted-foreground uppercase tracking-widest">Pricing</span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mt-2 mb-3">Start free, scale as you grow</h2>
          <p className="text-sm text-muted-foreground mb-3">Every plan includes GPT-4o-mini, knowledge base, and accent color customization.</p>
          <p className="text-xs text-muted-foreground/70 mb-8">Each chat bot is a unique AI assistant you embed on a different website.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="pricing-preview">
            {[
              { name: 'Free', price: '$0', feature: '1 bot · 15 msgs · 2-tab widget' },
              { name: 'Single', price: '$10', feature: '1 bot · 500 msgs · 4-tab widget' },
              { name: 'Starter', price: '$15', feature: '3 bots · 2K msgs · 4-tab widget' },
              { name: 'Pro', price: '$39', feature: '10 bots · 10K msgs · 4-tab widget', popular: true },
              { name: 'Business', price: '$99', feature: 'Unlimited · 50K msgs · White-label' },
            ].map((p) => (
              <div key={p.name} className={`p-4 rounded-xl border text-left ${p.popular ? 'border-[#10B981] bg-[#10B981]/5 relative' : 'border-border bg-card/50'}`}>
                {p.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#10B981] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Popular</span>}
                <p className="text-xs font-medium text-muted-foreground">{p.name}</p>
                <p className="text-xl font-bold text-foreground mt-1">{p.price}<span className="text-xs font-normal text-muted-foreground">{p.price !== '$0' ? '/mo' : ''}</span></p>
                <p className="text-xs text-muted-foreground mt-1">{p.feature}</p>
              </div>
            ))}
          </div>
          <Link to="/pricing" className="inline-flex items-center text-sm text-[#10B981] hover:underline mt-6 font-medium gap-1" data-testid="see-full-pricing">
            See full pricing <ChevronRight className="h-4 w-4" />
          </Link>
          <p className="text-xs text-muted-foreground/50 mt-3">KB = Knowledge Base — your custom Q&A pairs that train the bot</p>
        </section>

        {/* ─── FAQ ─── */}
        <section id="faq" className="max-w-3xl mx-auto pb-24" aria-label="FAQ">
          <div className="text-center mb-12">
            <span className="text-xs text-muted-foreground uppercase tracking-widest">FAQ</span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mt-2">Frequently asked questions</h2>
          </div>
          <div className="space-y-3" data-testid="faq-list">
            {FAQS.map((faq, i) => (
              <details key={i} className="group p-4 rounded-xl border border-border bg-card/50 cursor-pointer" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between text-sm font-medium text-foreground list-none">
                  {faq.q}
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90 shrink-0 ml-2" />
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ─── Final CTA ─── */}
        <section className="max-w-4xl mx-auto pb-24" aria-label="Call to action">
          <div className="p-10 sm:p-14 rounded-2xl bg-[#18181B] text-center relative overflow-hidden" data-testid="final-cta">
            <Spotlight className="-top-40 left-0 md:left-40 md:-top-20" fill="#10B981" />
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Ready to automate your customer support?</h2>
              <p className="text-neutral-400 text-sm sm:text-base max-w-lg mx-auto mb-8">Join hundreds of businesses using Whisper.AI to handle conversations 24/7. Free plan available, no credit card required.</p>
              <div className="flex flex-wrap justify-center gap-3">
                <button onClick={() => navigate(user ? '/dashboard' : '/signup')} className="px-8 py-3.5 rounded-xl bg-[#10B981] text-white text-sm font-medium hover:bg-[#059669] transition-colors flex items-center gap-2" data-testid="final-cta-btn">
                  {user ? 'Go to Dashboard' : 'Get Started Free'} <ArrowRight className="h-4 w-4" />
                </button>
                <button onClick={() => document.querySelector('[data-testid="chat-launcher-bubble"]')?.click()} className="px-8 py-3.5 rounded-xl border border-white/15 text-white/80 text-sm font-medium hover:bg-white/5 transition-colors" data-testid="final-demo-btn">
                  Talk to Our Bot
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ─── Footer ─── */}
      <footer className="relative z-10 border-t border-border py-10 px-6 sm:px-10" data-testid="site-footer">
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-5 gap-8 mb-8">
            <div className="sm:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <Logo size="sm" />
                <span className="text-sm font-bold text-foreground">Whisper.AI</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed max-w-xs">
                AI-powered chat widget platform. Deploy custom chatbots on any website in 60 seconds. Trusted by growing businesses worldwide.
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Product</p>
              <div className="space-y-2">
                <a href="#features" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Features</a>
                <Link to="/pricing" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Pricing</Link>
                <a href="#how-it-works" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
                <Link to="/guide" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Setup Guide</Link>
                <a href="#faq" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">FAQ</a>
                <Link to="/blog" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Company</p>
              <div className="space-y-2">
                <Link to="/signup" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Get Started</Link>
                <Link to="/login" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Sign In</Link>
                <a href="mailto:support@whisperai.one" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Contact Us</a>
                <a href="https://www.instagram.com/whisperai.one/" target="_blank" rel="noopener noreferrer" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Instagram</a>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">Legal</p>
              <div className="space-y-2">
                <Link to="/privacy" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
                <Link to="/cookies" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Cookie Policy</Link>
                <Link to="/refund" className="block text-xs text-muted-foreground hover:text-foreground transition-colors">Refund Policy</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-border pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} Whisper.AI. All rights reserved.</span>
            <div className="flex items-center gap-4">
              <a href="https://www.instagram.com/whisperai.one/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Instagram" data-testid="footer-instagram">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <span className="text-xs text-muted-foreground">Powered by WPO</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Home() {
  return (
    <>
      <LandingContent />
      <ChatWidget />
    </>
  );
}

// Routes
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/signup" element={<AuthPage mode="signup" />} />
      <Route path="/verify" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard/widgets/:widgetId" element={<ProtectedRoute><WidgetConfig /></ProtectedRoute>} />
      <Route path="/dashboard/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/dashboard/chat-history" element={<ProtectedRoute><ChatHistoryPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/:type" element={<PolicyPage />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <div className="App">
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            <CookieConsent />
            <AppRouter />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
